import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../utils/api.js";
import { STAGE_LABELS, STAGE_COLORS } from "../../utils/stages.js";
import { parseUTC } from "../../utils/date.js";

interface StageCounts {
  applied: number;
  assignment: number;
  review: number;
  interview: number;
  interview_complete: number;
  offer_pending: number;
  offer: number;
  rejected: number;
  withdrawn: number;
  talent_pool: number;
}

interface PipelineJob {
  slug: string;
  title: string;
  team: string;
  hiringManager: string;
  stages: StageCounts;
  total: number;
}

interface ActivityItem {
  id: number;
  candidateId: number;
  candidateName: string;
  jobSlug: string;
  jobTitle: string;
  fromStage: string | null;
  toStage: string;
  note: string | null;
  changedBy: string | null;
  createdAt: string;
}

interface OverdueAssignment {
  id: number;
  name: string;
  jobTitle: string;
  jobSlug: string;
  sentAt: string;
  deadline: string;
}

interface GlobalCounts {
  total: number;
  applied: number;
  assignment: number;
  review: number;
  interview: number;
  interview_complete: number;
  offer_pending: number;
  offer: number;
  rejected: number;
  withdrawn: number;
  talent_pool: number;
  hired: number;
}

interface DashboardData {
  pipeline: PipelineJob[];
  overdueAssignments: OverdueAssignment[];
  recentActivity: ActivityItem[];
  globalCounts: GlobalCounts;
}

const ACTIVE_STAGES = [
  "applied",
  "assignment",
  "review",
  "interview",
  "interview_complete",
  "offer_pending",
  "offer",
] as const;

const PIPELINE_CHIP_STAGES = [...ACTIVE_STAGES, "talent_pool"] as const;

function timeAgo(dateStr: string): string {
  const diff = Date.now() - parseUTC(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<DashboardData>("/dashboard")
      .then((d) => setData(d))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load dashboard"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="text-gray-400 animate-pulse">Loading dashboard...</div>;
  }

  if (error) {
    return <div className="text-red-600">Error: {error}</div>;
  }

  if (!data) return null;

  const { pipeline, overdueAssignments, recentActivity, globalCounts } = data;

  const totalActive = ACTIVE_STAGES.reduce((sum, s) => sum + globalCounts[s], 0);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Hiring Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">Pipeline overview across all open positions</p>
      </div>

      {/* Top row: funnel stages, earliest to latest */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <ActionCard
          label="Applied"
          count={globalCounts.applied}
          href="/candidates?stage=applied"
          color="blue"
        />
        <ActionCard
          label="Assignment"
          count={globalCounts.assignment}
          href="/candidates?stage=assignment"
          color="yellow"
        />
        <ActionCard
          label="Review"
          count={globalCounts.review}
          href="/candidates?stage=review"
          color="purple"
        />
        <ActionCard
          label="Interview"
          count={globalCounts.interview}
          href="/candidates?stage=interview"
          color="indigo"
        />
        <ActionCard
          label="Interview Complete"
          count={globalCounts.interview_complete}
          href="/candidates?stage=interview_complete"
          color="teal"
        />
      </div>

      {/* Bottom row: outcomes, alerts, and aggregates */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <ActionCard
          label="Offer Pending"
          count={globalCounts.offer_pending}
          href="/candidates?stage=offer_pending"
          color="emerald"
        />
        <ActionCard
          label="Hired"
          count={globalCounts.offer}
          href="/candidates?stage=offer"
          color="green"
        />
        <ActionCard
          label="Total Candidates"
          count={globalCounts.total}
          href="/candidates"
          color="gray"
          subtitle={`${totalActive} active`}
        />
        <ActionCard
          label="Overdue"
          count={overdueAssignments.length}
          href="/candidates?stage=assignment&assignment=Overdue"
          color="red"
        />
        <ActionCard
          label="Talent Pool"
          count={globalCounts.talent_pool}
          href="/candidates?stage=talent_pool"
          color="amber"
        />
        <ActionCard
          label="Rejected"
          count={globalCounts.rejected}
          href="/candidates?stage=rejected"
          color="red"
        />
        <ActionCard
          label="Withdrawn"
          count={globalCounts.withdrawn}
          href="/candidates?stage=withdrawn"
          color="slate"
        />
      </div>

      {/* Pipeline per job */}
      {pipeline.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-500">
          No open positions. Add a job markdown file to <code className="text-xs">jobs/</code> to
          get started.
        </div>
      ) : (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Pipeline by Position</h2>
          {pipeline.map((job) => (
            <div key={job.slug} className="rounded-lg border border-gray-200 bg-white p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">
                    <Link
                      to={`/jobs/${job.slug}/edit`}
                      className="hover:text-primary-700 hover:underline"
                    >
                      {job.title}
                    </Link>
                  </h3>
                  <p className="text-sm text-gray-500">
                    {job.team} &middot; {job.total} candidate{job.total !== 1 ? "s" : ""}
                  </p>
                </div>
                <Link
                  to={`/candidates?jobSlug=${job.slug}`}
                  className="text-sm text-primary-700 hover:text-primary-800"
                >
                  View all &rarr;
                </Link>
              </div>
              <div className="flex flex-wrap gap-2">
                {PIPELINE_CHIP_STAGES.map((stage) => {
                  const count = job.stages[stage];
                  return (
                    <Link
                      key={stage}
                      to={`/candidates?jobSlug=${job.slug}&stage=${stage}`}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium ${STAGE_COLORS[stage]} hover:opacity-80 transition-opacity`}
                    >
                      {STAGE_LABELS[stage]}
                      <span className="font-bold">{count}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent Activity */}
      {recentActivity.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Recent Activity</h2>
          <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-gray-100">
                {recentActivity.map((event) => (
                  <tr key={event.id}>
                    <td className="px-4 py-3 w-28">
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STAGE_COLORS[event.toStage] ?? "bg-gray-100 text-gray-600"}`}
                      >
                        {STAGE_LABELS[event.toStage] ?? event.toStage}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-900">
                        <Link
                          to={`/candidates/${event.candidateId}`}
                          className="font-medium hover:underline"
                        >
                          {event.candidateName}
                        </Link>
                        {event.fromStage && (
                          <span className="text-gray-500">
                            {" "}
                            moved from {STAGE_LABELS[event.fromStage] ?? event.fromStage}
                          </span>
                        )}
                        <span className="text-gray-400"> &middot; {event.jobTitle}</span>
                      </p>
                      {event.note && <p className="text-xs text-gray-500 mt-0.5">{event.note}</p>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap text-right">
                      {timeAgo(event.createdAt)}
                      {event.changedBy && ` by ${event.changedBy}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ActionCard({
  label,
  count,
  href,
  color,
  subtitle,
}: {
  label: string;
  count: number;
  href: string;
  color: string;
  subtitle?: string;
}) {
  const colorMap: Record<string, string> = {
    gray: "border-gray-300 bg-gray-100",
    blue: "border-blue-200 bg-blue-50",
    yellow: "border-yellow-200 bg-yellow-50",
    purple: "border-purple-200 bg-purple-50",
    indigo: "border-indigo-200 bg-indigo-50",
    teal: "border-teal-200 bg-teal-50",
    emerald: "border-emerald-200 bg-emerald-50",
    green: "border-green-200 bg-green-50",
    red: "border-red-200 bg-red-50",
    slate: "border-slate-200 bg-slate-50",
    amber: "border-amber-200 bg-amber-50",
  };
  const textMap: Record<string, string> = {
    gray: "text-gray-800",
    blue: "text-blue-700",
    yellow: "text-yellow-700",
    purple: "text-purple-700",
    indigo: "text-indigo-700",
    teal: "text-teal-700",
    emerald: "text-emerald-700",
    green: "text-green-700",
    red: "text-red-700",
    slate: "text-slate-600",
    amber: "text-amber-800",
  };

  return (
    <Link
      to={href}
      className={`min-w-0 rounded-lg border p-4 ${colorMap[color]} hover:opacity-90 transition-opacity`}
    >
      <div className={`text-3xl font-bold ${textMap[color]}`}>{count}</div>
      <div className={`text-sm font-medium ${textMap[color]}`}>{label}</div>
      {subtitle && <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>}
    </Link>
  );
}
